import { style } from '@vanilla-extract/css';

// An unset var() inherits, so a block with no writer color takes the post's text and fonts.
const HEADING_FONT = 'var(--wt-h2-font-family, var(--wt-header-font))';
const BODY_FONT = 'var(--wt-paragraph-font-family, var(--wt-body-font))';
const BUTTON_FONT = 'var(--wt-button-font-family, var(--wt-button-font))';

const TEXT = 'var(--tip-jar-text, var(--wt-paragraph-color))';
const HEADING_TEXT = 'var(--tip-jar-text, var(--wt-h2-font-color))';
const PROGRESS = 'var(--tip-jar-progress, var(--wt-button-background-color, var(--wt-primary-color, #030712)))';
const ON_PROGRESS = 'var(--tip-jar-on-progress, var(--wt-button-text-color, var(--wt-text-on-primary-color, #FFFFFF)))';

// Tints of the block's text, so they hold on any background. Browsers without color-mix get a mid grey instead.
const textTint = (percent: number) => `color-mix(in srgb, currentColor ${percent}%, transparent)`;
const MUTED = textTint(72);
const RULE = ['rgb(128 128 128 / 0.3)', textTint(12)];

export const container = style({
  padding: 'var(--wt-padding)',
});

// One inset with or without a card, so the amount buttons keep their shape when the writer sets a background.
export const block = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '32px 24px',
  borderTop: '1px solid',
  borderBottom: '1px solid',
  borderColor: RULE,
  color: TEXT,
  fontFamily: BODY_FONT,
  textAlign: 'center',
  '@media': {
    'screen and (max-width: 480px)': { padding: '28px 20px' },
  },
});

// Defined after `block` so its frame replaces the rules.
export const card = style({
  border: 'none',
  borderRadius: '16px',
  background: 'var(--tip-jar-background)',
});

export const heading = style({
  margin: 0,
  fontFamily: HEADING_FONT,
  fontSize: '28px',
  fontWeight: 700,
  lineHeight: '34px',
  letterSpacing: '-0.01em',
  color: HEADING_TEXT,
  textWrap: 'balance',
  '@media': {
    'screen and (max-width: 480px)': { fontSize: '24px', lineHeight: '30px' },
  },
});

export const subtitle = style({
  margin: '8px 0 0',
  fontSize: '16px',
  lineHeight: '24px',
  color: MUTED,
  textWrap: 'pretty',
});

export const progress = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  alignSelf: 'stretch',
  marginTop: '24px',
});

export const track = style({
  alignSelf: 'stretch',
  height: '6px',
  overflow: 'hidden',
  borderRadius: '999px',
  background: ['rgb(128 128 128 / 0.2)', textTint(10)],
});

export const fill = style({
  height: '100%',
  borderRadius: '999px',
  background: PROGRESS,
  transformOrigin: 'left center',
});

export const summary = style({
  margin: '12px 0 0',
  fontSize: '14px',
  lineHeight: '20px',
  color: MUTED,
  fontVariantNumeric: 'tabular-nums',
});

export const milestones = style({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '8px',
  margin: '12px 0 0',
  padding: 0,
  listStyle: 'none',
});

export const milestone = style({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 10px',
  borderRadius: '999px',
  color: MUTED,
  background: ['rgb(128 128 128 / 0.1)', textTint(6)],
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '16px',
});

export const milestoneReached = style({
  color: 'inherit',
});

export const milestoneCheck = style({
  color: PROGRESS,
});

export const buttons = style({
  display: 'grid',
  gridAutoFlow: 'column',
  gridAutoColumns: '1fr',
  alignSelf: 'stretch',
  gap: '8px',
  marginTop: '24px',
});

export const button = style({
  display: 'block',
  minWidth: 0,
  padding: '12px 8px',
  borderRadius: '999px',
  background: PROGRESS,
  color: ON_PROGRESS,
  fontFamily: BUTTON_FONT,
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '20px',
  textAlign: 'center',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontVariantNumeric: 'tabular-nums',
  transition: 'opacity 150ms ease',
  selectors: {
    '&:hover': { opacity: 0.9 },
  },
});

// A 44px tap target around the line of text, without the extra height moving anything.
export const otherAmount = style({
  display: 'inline-block',
  margin: '4px 0 -12px',
  padding: '12px 0',
  color: 'inherit',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
});

export const supporters = style({
  margin: '16px 0 0',
  fontSize: '14px',
  lineHeight: '20px',
  color: MUTED,
});
